import { useEffect, useRef, useState } from 'react';

/**
 * useActivityStream — live activity feed consumer for GET /api/activity/stream.
 *
 * The backend (Domain 02.2) exposes the org-wide activity feed as Server-Sent
 * Events: each humanized audit row is pushed as a named `activity` frame whose
 * `data:` is the SAME event object the pull endpoint (GET /api/activity) returns
 * — money-redacted and book-scoped SERVER-SIDE per the connected viewer, with
 * the internal organization_id stripped. The SSE `id:` is the audit row id, so
 * the browser resumes from Last-Event-ID on reconnect (no gap, no dupe).
 *
 * This hook is the FE consumer that REPLACES activity-feed polling: mount it
 * alongside the initial pull-endpoint snapshot (the graceful, no-JS/no-SSE
 * fallback) and it prepends live events as they arrive. It:
 *   - opens one EventSource with same-origin credentials (session cookie),
 *   - hands each parsed event to `onEvent` (latest callback, no re-subscribe),
 *   - reports a coarse connection `status` for a live indicator,
 *   - degrades gracefully: `unsupported` when EventSource is absent, and it
 *     leaves the browser's own auto-reconnect (server `retry:`) in place.
 */

export interface ActivityStreamEvent {
  id: string;
  timestamp: string;
  actor: string;
  entity_type: string;
  entity_id: string;
  entity_label: string;
  action: string;
  field: string | null;
  old_value?: string | null;
  new_value?: string | null;
  source?: string | null;
  message: string;
  // Activity layer (migration 102) enrichments — present on activity_events
  // frames (source: 'activity'), absent on audit-derived frames.
  event_type?: string;
  from_state?: string | null;
  to_state?: string | null;
}

export type ActivityStreamStatus = 'connecting' | 'open' | 'error' | 'unsupported';

interface UseActivityStreamOptions {
  /** Callback for each live event. Kept in a ref so changing it never re-opens the stream. */
  onEvent: (event: ActivityStreamEvent) => void;
  /** Gate the subscription (e.g. only open it for the role/view that shows the feed). */
  enabled?: boolean;
  /** Override the SSE endpoint (defaults to the org-wide stream). */
  path?: string;
}

export function useActivityStream(
  { onEvent, enabled = true, path = '/api/activity/stream' }: UseActivityStreamOptions,
): { status: ActivityStreamStatus } {
  const [status, setStatus] = useState<ActivityStreamStatus>('connecting');

  // Hold the latest onEvent without listing it as an effect dependency, so a
  // parent re-render (new closure each render) doesn't tear down and re-open the
  // EventSource — which would drop the Last-Event-ID resume position.
  const onEventRef = useRef(onEvent);
  useEffect(() => { onEventRef.current = onEvent; }, [onEvent]);

  useEffect(() => {
    if (!enabled) return;

    if (typeof EventSource === 'undefined') {
      setStatus('unsupported');
      return;
    }

    setStatus('connecting');
    const es = new EventSource(path, { withCredentials: true });

    const handleOpen = () => setStatus('open');

    const handleActivity = (ev: MessageEvent) => {
      try {
        const parsed = JSON.parse(ev.data) as ActivityStreamEvent;
        // Only forward a well-formed frame. The renderer (activityTitle) needs an
        // event_type OR an entity_type+action pair; a partial frame with just an id
        // would otherwise throw mid-render and trip the error boundary.
        if (parsed && parsed.id && (parsed.event_type || (parsed.entity_type && parsed.action))) {
          onEventRef.current(parsed);
        }
      } catch {
        /* ignore a malformed frame; the next one still arrives */
      }
    };

    const handleError = () => {
      // EventSource reconnects on its own (the server sends `retry:`), resuming
      // from Last-Event-ID. Reflect the transient drop as `connecting`; only a
      // terminally CLOSED stream is a hard `error` the fallback snapshot covers.
      setStatus(es.readyState === EventSource.CLOSED ? 'error' : 'connecting');
    };

    es.addEventListener('open', handleOpen);
    es.addEventListener('activity', handleActivity as EventListener);
    es.addEventListener('error', handleError);

    return () => {
      es.removeEventListener('open', handleOpen);
      es.removeEventListener('activity', handleActivity as EventListener);
      es.removeEventListener('error', handleError);
      es.close();
    };
  }, [enabled, path]);

  return { status };
}

/**
 * Small helper for consumers that prepend live events onto a bounded list:
 * dedupe by id (a reconnect replay may re-deliver an event already in the
 * initial snapshot) and cap the length.
 */
export function mergeActivityEvents<T extends { id: string }>(
  incoming: T[],
  existing: T[],
  cap = 200,
): T[] {
  const seen = new Set<string>();
  const out: T[] = [];
  for (const ev of [...incoming, ...existing]) {
    if (seen.has(ev.id)) continue;
    seen.add(ev.id);
    out.push(ev);
    if (out.length >= cap) break;
  }
  return out;
}
